import * as Yup from 'yup';
import { PAYMENT_METHOD, PAYMENT_PROVIDERS, RECIPIENT_TYPES } from 'components/common/constants';
import { getMaxDescriptionSize } from '../../utils';
import {
  LATIN_LETTERS_AND_DIGITS_REG_EXP,
  START_WITH_GB_REG_EXP,
  CYRILLIC_REGEXP,
  SWIFT_ROUTING_CODE_REG_EXP,
  getCustomerNameRegExp,
  getCompanyNameRegExp,
  getBankNameRegExp,
  getBicRegExp,
  getRegistrationNumberRegExp,
  getCityRegExp,
  getAddressRegExpByProviderAndMethod,
  validateCommentFieldByPaymentMethod,
  getAccountNumberRegExp,
  hasAtLeastTwoUniqueCharacters
} from './utils';
import { trimTextContent } from 'services/utils';
import { getConveraRequirements, getRoutingCodeError } from '../../converaRequirements';

export default ({
  i18n,
  onChange,
  onSubmit,
  customerNumber,
  model,
  currency,
  previousTransaction,
  providerData,
  paymentMethod,
  ibanCredentials,
  providerType,
  paymentPurposes
}) => ({
  validateOnChange: false,
  validateOnBlur: false,
  initialValues: {
    amount: `${previousTransaction?.amount || providerData?.amount || ''}`,
    recipient_wallet: previousTransaction?.counterparty?.iban || providerData?.recipient_wallet,
    recipient_type:
      RECIPIENT_TYPES[previousTransaction?.recipient_type] ||
      RECIPIENT_TYPES[previousTransaction?.counterparty?.party_type] ||
      providerData?.recipient_type ||
      RECIPIENT_TYPES.COMPANY,
    company_name: previousTransaction?.counterparty?.party_name || providerData?.company_name,
    first_name:
      (previousTransaction?.counterparty?.first_name ? previousTransaction?.counterparty?.first_name : null) ||
      (previousTransaction?.recipient_first_name ? previousTransaction?.recipient_first_name : null) ||
      previousTransaction?.recipient_name?.split(' ')[0] ||
      providerData?.first_name,
    last_name:
      (previousTransaction?.counterparty?.last_name ? previousTransaction?.counterparty?.last_name : null) ||
      (previousTransaction?.recipient_last_name ? previousTransaction?.recipient_last_name : null) ||
      previousTransaction?.recipient_name?.split(' ').slice(1).join(' ') ||
      providerData?.last_name,
    comment: previousTransaction?.comment || providerData?.comment,
    documents: null,
    registration_number: previousTransaction?.counterparty?.registration_number || providerData?.registration_number,
    beneficiary_city: previousTransaction?.counterparty?.party_city || providerData?.beneficiary_city,
    beneficiary_state: previousTransaction?.counterparty?.party_state || providerData?.beneficiary_state,
    beneficiary_post_code: previousTransaction?.counterparty?.post_code || providerData?.beneficiary_post_code,
    beneficiary_address: previousTransaction?.counterparty?.party_address || providerData?.beneficiary_address,
    beneficiary_country: previousTransaction?.counterparty?.party_country || providerData?.beneficiary_country,
    bank_country: previousTransaction?.counterparty?.bank_country || providerData?.bank_country,
    bank_name: previousTransaction?.counterparty?.bank_name || providerData?.bank_name,
    account_number: previousTransaction?.counterparty?.account_number || providerData?.account_number || '',
    bic: previousTransaction?.counterparty?.bic || providerData?.bic || '',
    sort_code: previousTransaction?.counterparty?.sort_code || providerData?.sort_code || '',
    payment_purpose: previousTransaction?.purpose || providerData?.payment_purpose || ''
  },
  initialStatus: { company_name: true, recipient_wallet: true },
  validationSchema: Yup.object().shape(
    {
      recipient_wallet: Yup.string().when('account_number', {
        is: (account_number) => !account_number,
        then: (schema) =>
          schema
            .nullable(true)
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(34, i18n.getMessage('client.validation.message.maxSize', { size: 34 }))
            .trim()
            // trick for using `currency` variable in checking value in validation
            .when('recipient_type', (recipient_type, schema) => {
              if (currency === 'GBP' && paymentMethod !== PAYMENT_METHOD.SWIFT) {
                return schema.matches(START_WITH_GB_REG_EXP, {
                  message: i18n.getMessage('client.validation.message.startWithGB')
                });
              }
              return schema;
            })
            .matches(LATIN_LETTERS_AND_DIGITS_REG_EXP, {
              message: i18n.getMessage('client.validation.message.isOnlyLatinLettersAndDigits')
            }),
        otherwise: (schema) => schema.nullable(true)
      }),
      bank_name: Yup.string().when('recipient_wallet', {
        is: (recipient_wallet) => !recipient_wallet,
        then: (schema) =>
          schema
            .nullable(true)
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(60, i18n.getMessage('client.validation.message.maxSize', { size: 60 }))
            .matches(getBankNameRegExp(), {
              message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                availableSymbols: "“.“, “,“, “-“, “/“, “(“, “)“, “'“, “+“, “Space“"
              })
            }),
        // Not required when an IBAN is present (bank_name is then resolved/prefilled from IBAN
        // lookup), but still character-restricted — an IBAN being present must never disable the
        // Latin/no-Cyrillic charset check, or bad data here reaches the backend unvalidated.
        otherwise: (schema) =>
          schema.nullable(true).matches(getBankNameRegExp(), {
            message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
              availableSymbols: "“.“, “,“, “-“, “/“, “(“, “)“, “'“, “+“, “Space“"
            })
          })
      }),
      account_number: Yup.string().when('recipient_wallet', {
        is: (recipient_wallet) => !recipient_wallet,
        then: (schema) =>
          schema
            .nullable(true)
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(50, i18n.getMessage('client.validation.message.maxSize', { size: 50 }))
            .min(7, i18n.getMessage('client.validation.message.minSize', { size: 7 }))
            .matches(getAccountNumberRegExp(), {
              message: i18n.getMessage('transfer.form.error.accountNumber')
            }),
        // See bank_name above — same reasoning.
        otherwise: (schema) =>
          schema.nullable(true).matches(getAccountNumberRegExp(), {
            message: i18n.getMessage('transfer.form.error.accountNumber')
          })
      }),
      bic: Yup.string().when('recipient_wallet', {
        is: (recipient_wallet) => !recipient_wallet,
        then: (schema) =>
          schema
            .nullable(true)
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(11, i18n.getMessage('client.validation.message.maxSize', { size: 11 }))
            .min(8, i18n.getMessage('client.validation.message.minSize', { size: 8 }))
            .matches(getBicRegExp(), {
              message: i18n.getMessage('client.validation.message.isOnlyUppercaseLatinLettersAndDigits')
            }),
        // See bank_name above — same reasoning.
        otherwise: (schema) =>
          schema.nullable(true).matches(getBicRegExp(), {
            message: i18n.getMessage('client.validation.message.isOnlyUppercaseLatinLettersAndDigits')
          })
      }),
      registration_number: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
        .matches(getRegistrationNumberRegExp(), {
          message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
            availableSymbols: "“.“, “,“, “-“, “/“, “(“, “)“, “'“, “+“, “Space“"
          })
        }),
      // Routing code is only rendered on the NON_IBAN tab (see OpenPaydAdvancedForm) — same
      // `recipient_wallet` presence check the other manual-bank-entry fields above use, so a value
      // inherited from a repeat payment doesn't get validated (and block submit) while on the IBAN tab.
      sort_code: Yup.string()
        .nullable(true)
        .test('sort-code', function (value) {
          const { recipient_wallet, bank_country } = this.parent;
          // TRN-7602: Convera API / Merchant — mandatory for some bank countries (one rule: 4-11 letters and digits)
          const { isRoutingCodeRequired } = getConveraRequirements({
            provider: providerType,
            paymentMethod,
            bankCountry: ibanCredentials?.bank_country || bank_country,
            hasIban: !!recipient_wallet
          });

          if (isRoutingCodeRequired) {
            const routingCodeError = getRoutingCodeError(value);
            if (routingCodeError === 'empty') {
              return this.createError({ message: i18n.getMessage('client.validation.message.isEmpty') });
            }
            return routingCodeError
              ? this.createError({ message: i18n.getMessage('client.validation.message.routingCodeConvera') })
              : true;
          }

          // Otherwise optional, and only rendered on the NON_IBAN tab (see OpenPaydAdvancedForm) — the same
          // `recipient_wallet` presence check the other manual-bank-entry fields above use, so a value inherited from a
          // repeat payment doesn't get validated (and block submit) while on the IBAN tab.
          if (!recipient_wallet && paymentMethod === PAYMENT_METHOD.SWIFT && value) {
            return SWIFT_ROUTING_CODE_REG_EXP.test(value)
              ? true
              : this.createError({ message: i18n.getMessage('client.validation.message.routingCodeFormat') });
          }

          return true;
        }),
      beneficiary_city: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
        .matches(getCityRegExp(), {
          message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
            availableSymbols: '“-“'
          })
        }),
      beneficiary_post_code: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(15, i18n.getMessage('client.validation.message.maxSize', { size: 15 }))
        .matches(getCityRegExp(), {
          message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
            availableSymbols: '“-“'
          })
        }),
      beneficiary_state: Yup.string()
        .nullable(true)
        .max(100, i18n.getMessage('client.validation.message.maxSize', { size: 100 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        )
        // TRN-7602: Convera API / Merchant — mandatory for some bank countries
        .test('state-required', i18n.getMessage('client.validation.message.isEmpty'), function (value) {
          const { isStateRequired } = getConveraRequirements({
            provider: providerType,
            paymentMethod,
            bankCountry: ibanCredentials?.bank_country || this.parent.bank_country,
            hasIban: !!this.parent.recipient_wallet
          });

          return !isStateRequired || !!trimTextContent(value);
        }),
      beneficiary_address: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .min(2, i18n.getMessage('client.validation.message.minSize', { size: 2 }))
        .max(140, i18n.getMessage('client.validation.message.maxSize', { size: 140 }))
        .matches(getAddressRegExpByProviderAndMethod(providerType, paymentMethod), {
          message:
            paymentMethod === PAYMENT_METHOD.SWIFT &&
            [
              PAYMENT_PROVIDERS.MANUAL,
              PAYMENT_PROVIDERS.CONVERA,
              PAYMENT_PROVIDERS.CONVERA_MERCHANT,
              PAYMENT_PROVIDERS.CONVERA_API
            ].includes(providerType)
              ? i18n.getMessage('transfer.form.error.address.swift.restricted')
              : i18n.getMessage('transfer.form.error.address')
        })
        .test('address-validation', i18n.getMessage('transfer.form.error.address'), (address) =>
          hasAtLeastTwoUniqueCharacters(trimTextContent(address))
        ),
      amount: Yup.string().nullable(true).required(i18n.getMessage('client.validation.message.isEmpty')),
      recipient_type: Yup.string().nullable(false).required(i18n.getMessage('client.validation.message.isEmpty')),
      company_name: Yup.string()
        .nullable(true)
        .when('recipient_type', {
          is: RECIPIENT_TYPES.COMPANY,
          then: (schema) =>
            schema
              .required(i18n.getMessage('client.validation.message.isEmpty'))
              .max(70, i18n.getMessage('client.validation.message.maxSize', { size: 70 }))
              .matches(getCompanyNameRegExp(), {
                message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                  availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “&“, “CrLf“, “Space“"
                })
              }),
          otherwise: (schema) => schema.nullable(true)
        }),
      first_name: Yup.string()
        .nullable(true)
        .when('recipient_type', {
          is: RECIPIENT_TYPES.INDIVIDUAL,
          then: (schema) =>
            schema
              .required(i18n.getMessage('client.validation.message.isEmpty'))
              .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
              .matches(getCustomerNameRegExp(), {
                message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                  availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
                })
              }),
          otherwise: (schema) => schema.nullable(true)
        }),
      last_name: Yup.string()
        .nullable(true)
        .when('recipient_type', {
          is: RECIPIENT_TYPES.INDIVIDUAL,
          then: (schema) =>
            schema
              .required(i18n.getMessage('client.validation.message.isEmpty'))
              .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
              .matches(getCustomerNameRegExp(), {
                message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                  availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
                })
              }),
          otherwise: (schema) => schema.nullable(true)
        }),
      comment: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .test(
          'openpayd-comment-validation',
          paymentMethod === PAYMENT_METHOD.SWIFT
            ? i18n.getMessage('transfer.form.error.comment.SWIFT')
            : i18n.getMessage('transfer.form.error.comment.OTHER_METHODS'),
          (comment) => validateCommentFieldByPaymentMethod(paymentMethod, trimTextContent(comment))
        )
        .max(
          getMaxDescriptionSize(paymentMethod),
          i18n.getMessage('client.validation.message.maxSize', {
            size: getMaxDescriptionSize(paymentMethod)
          })
        ),
      payment_purpose: Yup.string().when('bank_country', (bank_country, schema) => {
        const hasPaymentPurposeOptions = !!paymentPurposes?.find((item) => item.transferProvider === providerType)
          ?.paymentPurposes?.[bank_country];
        if (bank_country === 'AE' && hasPaymentPurposeOptions) {
          return schema.required(i18n.getMessage('client.validation.message.isEmpty'));
        }
        return schema.nullable(true);
      })
    },
    [['account_number', 'recipient_wallet']]
  ),
  onSubmit: (values) => {
    onChange(values);
    onSubmit({ customerNumber, providerData: values, model });
  }
});
